import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import InstagramCommentIcon from '@src/svgs/InstagramCommentIcon';
import InstagramHeartIcon from '@src/svgs/InstagramHeartIcon';
import InstagramRepostIcon from '@src/svgs/InstagramRepostIcon';
import InstagramShareIcon from '@src/svgs/InstagramShareIcon';
import InstagramVerifiedBadge from '@src/svgs/InstagramVerifiedBadge';
import PlusIcon from '@src/svgs/PlusIcon';
import React from 'react';
import styled from 'styled-components';

import { BrandHandle, BrandLogo, StoryDescription } from '../BrandSlots';
import { useChromeFontSize } from '../useChromeFontSize';
import { OverlayFrame } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.instagramShortFormVideo;

/**
 * Instagram Reels overlay — paints Reels' full-screen vertical viewer chrome
 * over the clip-preview rect.
 *
 * Top strip: `+` glyph on the left, a centered two-tab row
 * (**Reels** active / Friends inactive with a small face-cluster glyph beside
 * it), and a right-anchored sliders-with-hearts filter glyph (Reels'
 * preferences affordance — surface-specific, inlined here rather than added
 * to `src/svgs/`).
 *
 * Right rail (top → bottom): like, comment, repost (circular arrows), send
 * (paper plane), more (ellipsis), and an account tile (small rounded-square
 * thumbnail of `<BrandLogo />` reading as the "audio source" affordance on
 * the real Reels surface). Counts live under like / comment / repost only —
 * send, more, and the account tile have no count below.
 *
 * Bottom strip: avatar + handle + verified tick + outlined Follow pill on
 * row one, single-line description preview with `...more` pinned right on
 * row two. No music-attribution row — Reels paints one only when the creator
 * attached a sound, and we don't model that variant in Phase 1; the rail's
 * account tile already reads as the audio-source affordance.
 *
 * No captions. Reels has no platform-level CC toggle on the viewer side —
 * whatever a creator bakes into the video plays as-is — so the overlay
 * declares no `showCaptions` variant. No `showPlayControls` either: Reels
 * paints zero persistent player chrome; the thin bottom scrubber line is the
 * only playback affordance. Same posture as TikTok.
 *
 * Scrubber: always-on thin `0.25em` line at the very bottom driving
 * `SafeZonePlaybackBridge` via the shared `useScrubberDrag` hook. No thumb
 * dot, matching the other Shorts overlays.
 *
 * 9:16 only — Reels is vertical-only on the real surface.
 *
 * Scaling matches the other Shorts overlays so all four read at the same
 * visual weight on identical rects: chrome sized in `em` against a base
 * font-size derived from `min(rect.width / 360, rect.height / 640)`,
 * clamped `[0.05, 3]`.
 */
const REFERENCE_RECT_WIDTH = 360;
const REFERENCE_RECT_HEIGHT = 640;
const REFERENCE_FONT_PX = 10;
const SCALE_FLOOR = 0.05;
const SCALE_MAX = 3;

export const InstagramReelsOverlay: React.FC<SafeZoneProps> = ({
  rect,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_RECT_WIDTH,
    referenceHeight: REFERENCE_RECT_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_FLOOR,
    scaleMax: SCALE_MAX,
  });

  // Always-on bottom scrubber — same shared hook every Shorts overlay uses
  // so seek behavior stays in lockstep across the vertical short-form
  // family.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);
  const duration = playback?.duration ?? 0;

  return (
    <OverlayFrame
      data-testid="instagram-reels-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      <TopStrip>
        <TopPlusBtn type="button" aria-label={COPY.aria.create}>
          <PlusIcon width="2.2em" height="2.2em" strokeColor="#ffffff" />
        </TopPlusBtn>

        <TopTabs>
          <TopTabActiveWrap>
            <TopTabActive aria-label={COPY.aria.reelsTab}>
              {COPY.tabs.reels}
            </TopTabActive>
            <TopTabActiveUnderline aria-hidden="true" />
          </TopTabActiveWrap>
          <TopTabFriendsWrap>
            <TopTabInactive aria-label={COPY.aria.friendsTab}>
              {COPY.tabs.friends}
            </TopTabInactive>
            {/* Two-face cluster glyph beside "Friends" — Reels paints a
                small overlapping-circle cluster as the affordance for the
                "friends" feed. Inline SVG since it's a one-off surface
                element. `currentColor` plus the parent's `color: #ffffff`
                keeps the strokes white. */}
            <FriendsFaces aria-hidden="true">
              <svg width="2.4em" height="1.4em" viewBox="0 0 24 14" fill="none">
                {/* Fills stay explicit `rgba(255,255,255,…)` — they're
                    translucency overlays, not the icon color; stroking via
                    `currentColor` is what binds the glyph to the surrounding
                    `color: #ffffff` on the overlay frame. */}
                <circle
                  cx="7"
                  cy="7"
                  r="6"
                  fill="rgba(255,255,255,0.4)"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <circle
                  cx="17"
                  cy="7"
                  r="6"
                  fill="rgba(255,255,255,0.7)"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            </FriendsFaces>
          </TopTabFriendsWrap>
        </TopTabs>

        {/* Sliders-with-hearts filter glyph — Reels' preferences affordance.
            Surface-specific; inline rather than under src/svgs/. Two stacked
            slider tracks, each with a small heart-shaped knob. */}
        <TopFilterBtn type="button" aria-label={COPY.aria.preferences}>
          <svg width="2.4em" height="2.4em" viewBox="0 0 24 24" fill="none">
            {/* Strokes + heart fills resolve through `currentColor` so the
                glyph picks up the surrounding `color: #ffffff` on the
                overlay frame — same posture as the shared icons under
                `src/svgs/`. */}
            <path
              d="M3 7h6m4 0h8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M3 17h12m4 0h2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M11 5.4c-.7-1.3-2.6-1.3-3.3 0-.7 1.3.3 2.7 1.65 3.4 1.35-.7 2.35-2.1 1.65-3.4Z"
              fill="currentColor"
            />
            <path
              d="M18.65 15.4c-.7-1.3-2.6-1.3-3.3 0-.7 1.3.3 2.7 1.65 3.4 1.35-.7 2.35-2.1 1.65-3.4Z"
              fill="currentColor"
            />
          </svg>
        </TopFilterBtn>
      </TopStrip>

      <RightRail>
        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.like}>
            <InstagramHeartIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.like}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.comment}>
            {/* Mirror the comment glyph horizontally so the speech-bubble
                tail points the same way Instagram paints it on the real
                surface — same trick `InstagramFeedPostOverlay` uses. */}
            <FlippedGlyph>
              <InstagramCommentIcon
                width="2.6em"
                height="2.6em"
                fillColor="#ffffff"
              />
            </FlippedGlyph>
          </ActionIconBtn>
          <ActionCount>{COPY.counts.comment}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.repost}>
            <InstagramRepostIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.repost}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.send}>
            <InstagramShareIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.more}>
            <EllipsisIcon width="2.4em" height="0.55em" fillColor="#ffffff" />
          </ActionIconBtn>
        </ActionItem>
      </RightRail>

      {/* Account tile — small rounded-square thumbnail of the brand logo.
          Reads as the "audio source" affordance on the real Reels surface.
          Anchored separately from the rail (the way TikTok's sound disc is)
          so its vertical center lines up with the bottom strip's
          description row — same baseline as the `…more` link to its left,
          which matches what Reels paints on the real surface. Square (not
          circular) and smaller than the bottom-strip avatar (2.6em vs 3em)
          so the two don't visually duplicate. */}
      <AccountTileBtn type="button" aria-label={COPY.aria.audioSource}>
        <AccountTileFrame>
          <AccountTileInner>
            <BrandLogo />
          </AccountTileInner>
        </AccountTileFrame>
      </AccountTileBtn>

      <BottomStrip>
        <BrandRow>
          <Avatar>
            <BrandLogo />
          </Avatar>
          <BrandHandleText>
            <BrandHandle />
          </BrandHandleText>
          <VerifiedTick aria-label={COPY.aria.verified}>
            <InstagramVerifiedBadge width="1.4em" height="1.4em" />
          </VerifiedTick>
          <FollowPill type="button" aria-label={COPY.aria.follow}>
            {COPY.followLabel}
          </FollowPill>
        </BrandRow>

        <DescriptionPreview>
          <StoryDescription fallback={COPY.descriptionFallback} />
          {/*
            "...more" pinned to the right of the description row. Same
            pattern TikTok / YouTube Shorts use — `font-size: inherit` so
            it tracks the description's scaled font.
          */}
          <MoreLink>{COPY.moreLink}</MoreLink>
        </DescriptionPreview>
      </BottomStrip>

      {/* Always-on thin scrubber at the very bottom — no thumb dot.
          Pointer-events activate when the playback bridge is ready,
          matching every other Shorts overlay. */}
      <ScrubberRail
        ref={scrubberRef}
        role="slider"
        aria-label={COPY.aria.seek}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, duration)}
        aria-valuenow={Math.max(0, displayTime)}
        onMouseDown={onScrubberMouseDown}
        $interactive={scrubberInteractive}
      >
        <ScrubberFill style={{ width: `${progressPct}%` }} />
      </ScrubberRail>
    </OverlayFrame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 360×640 reference).
// ---------------------------------------------------------------------------

// Top strip — plus glyph on the left, centered tab cluster, filter glyph
// pinned to the right. The cluster is absolutely centered (relative to the
// rect) so the side buttons' widths don't nudge it off-center.
const TopStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5em;
  padding: 1.4em 1.4em 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const TopPlusBtn = styled.button`
  position: absolute;
  top: 1.4em;
  left: 1.4em;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));

  /* Bump the plus glyph's stroke so it reads at the same weight as the
     surrounding bold tab text. The shared PlusIcon ships at the default
     stroke-width: 1, which looks anemic next to the 700-weight "Reels"
     wordmark. */
  & svg path {
    stroke-width: 2.4;
  }
`;

// Absolutely-centered tab cluster — the two top-bar side buttons are
// `position: absolute` so they don't push the centered cluster off-center.
const TopTabs = styled.div`
  position: absolute;
  top: 1.4em;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1.6em;
`;

// Active tab is paired with an underline rendered as a sibling — wrapping
// them in a flex column so the underline pins directly under the tab text
// regardless of the tab's intrinsic width. Same trick TikTok uses for the
// "For You" tab.
const TopTabActiveWrap = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3em;
`;

const TopTabActive = styled.span`
  font-size: 1.7em;
  font-weight: 700;
  letter-spacing: 0.005em;
  color: #ffffff;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

const TopTabActiveUnderline = styled.span`
  width: 1.6em;
  height: 0.16em;
  background: #ffffff;
  border-radius: 0.08em;
`;

// "Friends" tab + the two-face cluster glyph beside it. The cluster reads
// as part of the tab affordance, so they sit on the same baseline.
const TopTabFriendsWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
`;

const TopTabInactive = styled.span`
  font-size: 1.7em;
  font-weight: 600;
  letter-spacing: 0.005em;
  color: rgba(255, 255, 255, 0.65);
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

const FriendsFaces = styled.span`
  display: inline-flex;
  align-items: center;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

const TopFilterBtn = styled.button`
  position: absolute;
  top: 1.4em;
  right: 1.4em;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Right-side action rail. Bottom-anchored above the bottom strip's two-row
// layout. `z-index: 1` lifts it above the bottom strip's gradient backdrop
// so the rail's white icons stay legible where they overlap the strip's
// text. Tighter gap than TikTok's rail (1.4em vs 1.6em) because Reels packs
// one more rail item (the account tile) at the bottom.
const RightRail = styled.div`
  position: absolute;
  right: 0.8em;
  bottom: 5.6em;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4em;
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
`;

// Thicken every stroked path inside the rail's icons so the rail reads
// uniformly across glyphs with different intrinsic stroke widths. Same
// trick TikTok / LinkedIn Shorts / YouTube Shorts use; fill-only glyphs
// have no `stroke` attribute, so the override harmlessly no-ops on them.
const ActionIconBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));

  & svg path,
  & svg circle,
  & svg rect {
    stroke-width: 2;
  }
`;

// Mirrors the comment glyph horizontally — Instagram's speech-bubble tail
// points the opposite way from our shared `InstagramCommentIcon`.
const FlippedGlyph = styled.span`
  display: inline-flex;
  transform: scaleX(-1);
`;

const ActionCount = styled.span`
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Account tile — small rounded-square thumbnail of the brand logo. Reads
// as the "audio source" affordance on the real Reels surface. Different
// shape from the bottom-strip avatar (square here, circular below) so the
// two don't visually duplicate. Same posture as TikTok's sound-disc.
//
// Anchored to the rect (not the rail) so its vertical center sits on the
// description row's baseline — directly across from the `…more` link.
// Position math: BottomStrip sits at `bottom: 0.25em` with `padding-bottom:
// 1.4em`; the description row is its lower row, height ≈ font-size
// (1.15em) × line-height (1.35) ≈ 1.55em, so the row spans ≈ 1.65em → 3.2em
// from rect bottom. Centering the 2.6em tile on ≈ 2.4em → `bottom: 1.1em`.
// `right: 0.8em` matches the rail's right edge so the tile shares the rail
// column.
const AccountTileBtn = styled.button`
  position: absolute;
  right: 0.8em;
  bottom: 1.1em;
  z-index: 1;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

const AccountTileFrame = styled.span`
  display: inline-flex;
  width: 2.6em;
  height: 2.6em;
  border-radius: 0.5em;
  background: #1a1a1a;
  padding: 0.15em;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 0.1em rgba(255, 255, 255, 0.85) inset;
`;

const AccountTileInner = styled.span`
  display: inline-block;
  width: 100%;
  height: 100%;
  border-radius: 0.35em;
  overflow: hidden;
`;

// Bottom strip — brand row + description preview. `bottom` matches the
// scrubber rail's height (0.25em) so the strip's lower edge sits flush
// against the scrubber's top edge. Padding right reserves clearance for the
// right rail so text never tucks underneath it.
const BottomStrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.25em;
  padding: 1.4em 5.5em 1.4em 1.6em;
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.25) 60%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7em;
`;

// Circular avatar — smaller than LinkedIn Shorts' 4.2em (which spans two
// text rows) since Reels' brand row is a single line; matches the rest of
// the chrome under the viewport-derived font scale.
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 3em;
  height: 3em;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.1em rgba(255, 255, 255, 0.85) inset;
`;

// `BrandHandle` itself includes the leading `@` (see `BrandSlots.tsx`),
// so the slot renders unprefixed here.
const BrandHandleText = styled.span`
  display: block;
  font-size: 1.3em;
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VerifiedTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Outlined Follow pill — translucent-white border, no fill. Distinct from
// LinkedIn Shorts' translucent-white pill and TikTok's red-outlined pill.
const FollowPill = styled.button`
  pointer-events: auto;
  flex: 0 0 auto;
  font-size: 1.05em;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.35em 0.9em;
  border: 0.1em solid #ffffff;
  border-radius: 0.5em;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// `position: relative` so the absolutely-positioned `MoreLink` anchors to
// the description row. Single-line clamp — Reels paints the description as
// a single line with `...more` at the right; tapping expands it (out of
// scope here).
const DescriptionPreview = styled.div`
  position: relative;
  font-size: 1.15em;
  font-weight: 400;
  line-height: 1.35;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  padding-right: 4em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MoreLink = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: inherit;
  line-height: 1.35;
  opacity: 0.85;
`;

// Thin always-on scrubber line — matches the other Shorts overlays.
// Pointer-events flip to `auto` only when there's a real bridge to drive.
const ScrubberRail = styled.div<{ $interactive: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 0.25em;
  background: #5a5a5a;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
`;
